import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import {
  boxesFor,
  ruleShape,
  swampingHint,
  type Box,
} from "@/pages/PortfolioBuilder/builderModel";
import MatchBox from "@/pages/PortfolioBuilder/components/builder/MatchBox";
import RuleSpine from "@/pages/PortfolioBuilder/components/builder/RuleSpine";
import OperatorSwitch from "@/pages/PortfolioBuilder/components/builder/OperatorSwitch";
import TermChip from "@/pages/PortfolioBuilder/components/builder/TermChip";
import type { HeadlineQueryPlan } from "./types";

/** A list the combined watchlist links: one the plan makes, or the desk's. */
export interface PreviewList {
  name: string;
  tickers: string[];
  keywords: string[];
  color?: string | null;
}

interface CombinedPreviewProps {
  query: HeadlineQueryPlan;
  lists: PreviewList[];
  /** Its tags, named: its own and those of the tag lists it includes. */
  tags: { id: string; name: string }[];
  /** Newsquawk tag lists it includes, which have only a name here. */
  tagListNames: string[];
}

/** Tags shown before "+N more": a company list can bring dozens. */
const TAG_CAP = 8;

const MENU_TAG_IDS = new Set(
  menuCategories.flatMap((group) => group.items.map((item) => String(item.id)))
);

/**
 * A combined watchlist from a concierge plan, drawn the way the builder draws
 * it — the same boxes, chips, switches and "either / both of these" frame —
 * but read-only. It's the last thing in the preview because it is what
 * "Build it" makes and "Open in builder" opens.
 */
export default function CombinedPreview({ query, lists, tags, tagListNames }: CombinedPreviewProps) {
  const inBox = (box: Box) =>
    lists.filter((list) =>
      boxesFor({
        keywords: list.keywords.length > 0,
        tickers: list.tickers.length > 0,
        tags: false,
      }).includes(box)
    );
  const keywordLists = inBox("keywords");
  const tickerLists = inBox("tickers");
  const expression = query.search_keyword.trim();

  const filled = {
    keywords: keywordLists.length > 0 || !!expression,
    tickers: tickerLists.some((list) => list.tickers.length > 0),
    tags: tags.length > 0 || tagListNames.length > 0,
  };
  const shape = ruleShape({
    filled,
    keywordOp: query.keyword_op,
    filterMode: query.filter_op,
  });
  const hint = swampingHint({
    frame: shape.frame,
    typedTickers: 0,
    tickerLists: tickerLists.map((list) => list.name),
    // Only the curated tags are known to be topics here; the rest stay quiet
    // rather than guess.
    tags: tags.map((tag) => ({ name: tag.name, broad: MENU_TAG_IDS.has(tag.id) })),
  });

  const listChips = (items: PreviewList[]) =>
    items.map((list) => (
      <TermChip key={list.name} kind="list" label={list.name} dot={list.color ?? undefined} />
    ));

  const shownTags = tags.slice(0, TAG_CAP);
  const moreTags = tags.length - shownTags.length;

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-xs text-gray-400">Matches headlines that…</p>
      <RuleSpine
        keywords={
          filled.keywords ? (
            <MatchBox
              label="Keywords"
              title="mention any of these keywords"
              field={
                <>
                  {listChips(keywordLists)}
                  {expression && <TermChip kind="keyword" label={expression} />}
                </>
              }
            />
          ) : undefined
        }
        tickers={
          tickerLists.length > 0 ? (
            <MatchBox
              label="Tickers"
              title="are about any of these tickers"
              field={listChips(tickerLists)}
            />
          ) : undefined
        }
        tags={
          filled.tags ? (
            <MatchBox
              label="Tags"
              title="are tagged with any of these"
              field={
                <>
                  {shownTags.map((tag) => (
                    <TermChip key={tag.id} kind="tag" label={tag.name} />
                  ))}
                  {moreTags > 0 && (
                    <span className="px-1 text-xs text-gray-400">
                      +{moreTags.toLocaleString()} more
                    </span>
                  )}
                  {tagListNames.map((name) => (
                    <TermChip key={name} kind="list" label={name} />
                  ))}
                </>
              }
            />
          ) : undefined
        }
        keywordSwitch={
          <OperatorSwitch
            label="How keywords combine with tickers and tags"
            state={shape.keywordOp}
            value={query.keyword_op}
            readOnly
          />
        }
        filterSwitch={
          <OperatorSwitch
            label="How tickers combine with tags"
            state={shape.filterMode}
            value={query.filter_op}
            readOnly
          />
        }
        frame={shape.frame}
        hint={hint}
      />
    </div>
  );
}
